/**
 * useProjectAccess — centralised permission tiers for project-scoped actions.
 *
 * Hierarchy:
 *
 *   adminOnly          -> workspace admins only
 *   adminAndManager    -> admins + org-wide managers (no project scope)
 *   adminAndPM         -> admins + the project lead
 *   adminManagerAndPM  -> admins + project lead + managers who are explicit
 *                         project members (not every manager in the org)
 *
 * Access paths (mirrors backend visible_project_ids):
 *   1. Workspace admin or manager            → bypass, see all projects
 *   2. Direct ProjectMembership (is_active)  → isMember = true
 *   3. Member of an attached department      → isMember = true (via_dept_id set)
 *   4. Designated project lead               → isPM = true
 *
 * Pass `members` (the project's ProjectMembership list, including dept-derived
 * rows) to enable the member and project-scoped manager checks.
 */
import { useAuth } from "@/contexts/AuthContext";

export function useProjectAccess(project, members = []) {
  const { user } = useAuth();

  const role = user?.role ?? "";
  const isAdmin   = role === "admin";
  const isManager = role === "manager";
  const isPM      = !!project?.project_lead_user_id &&
                    user?.id === project.project_lead_user_id;

  // Any active membership (direct or dept-derived) — covers access paths 2 & 3
  const isMember = members.some(
    (m) => m.user_id === user?.id && m.is_active !== false,
  );

  // Manager with an explicit active ProjectMembership on this project
  const isProjectManager = isManager && isMember;

  // True when the user has any form of project access
  const canView = isAdmin || isManager || isPM || isMember;

  return {
    /** Only workspace admins. */
    adminOnly: isAdmin,

    /** Admins and org-wide managers. */
    adminAndManager: isAdmin || isManager,

    /** Admins and the project lead. */
    adminAndPM: isAdmin || isPM,

    /** Admins, global managers, or the project lead. */
    adminManagerOrPM: isAdmin || isManager || isPM,

    /** Admins, the project lead, or managers with explicit project access. */
    adminManagerAndPM: isAdmin || isPM || isProjectManager,

    /** Any project access: admin, manager, lead, or any active member. */
    canView,

    // Raw flags
    isAdmin,
    isManager,
    isPM,
    isProjectManager,
    isMember,
  };
}
